/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-empty-function */
import Button from "@Components/Button";
import styles from "./PropertyCTAs.module.scss";
import { useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES, HAPPY_FLOW_TENANCY_CTAS, PAGE } from "constants/index";
import gtagEventEmitter, { gtagDuplicateEventEmitter } from "helpers/gtagEventEmitter";
import { useAnalytics } from "helpers/analytics";
import LeadFormDispatcher from "@Components/LeadFormModal/actions";
import ChatLinks from "../ChatLinks";
import PropertyDetailsModal from "../PropertyDetailsModal";
import { viewRoomsClickHandler } from "@Pages/PropertyPage/data/helper";
import BottomSheetOverlay from "@Components/BottomSheetOverlay";
import handleLeadAPICall from "@Components/LeadFormV2/utils/handleLeadAPICall";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import ShortlistButton from "@Pages/Search/components/SearchListItem/components/ShortListButton";
import ShareIcon from "@Icons/ShareIcon";
import PropertyPageDispatcher from "@Pages/PropertyPage/data/actions";
import MetaFacts from "../PropertyDetailsSection/components/MetaFacts";
import { useHistory } from "hooks/useHistory";
import getBookFormLink from "./utils/getBookFormLink";
import useIsHappyFlow from "hooks/useIsHappyFlow";
import Skeleton from "@Components/Skeleton";
import getPaymentFormLink from "./utils/getPaymentFormLink";
import {
  getAvailableStartingPricingHelper,
  getPricingCurrency,
  getPricingDuration,
} from "../RoomTypeSection/utils";
import viewTransitionNavUtility from "@Utils/viewTransitionNavUtility";
import useIsHappyFlowBookForm from "hooks/useIsHappyFlowBookForm";
import isPaymentActive from "@Utils/isPaymentActive";
import { useFeatureIsOn, useFeatureValue } from "@growthbook/growthbook-react";
import { actionPayload } from "@App/userHistoryReducer";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import config from "@Config/index";
import WeChatButton from "@Components/WeChatButton";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import { convertToWhitelabelURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import LottieAnimator from "@Components/LottieAnimator";
import useIsApp from "hooks/useIsApp";
import { APP_PAGE, APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import { logAppEvent } from "@Utils/appAnalytics";
import useIsTabletPortrait from "hooks/useIsTabletPortrait";
import { isWhitelabelPartnerInventoryFunc } from "@Utils/whitelabelHelper";
import { SCREEN_SIZE, useResponsiveness } from "hooks/useResponsiveness";
import { getCurrencySymbol } from "../PriceTrends/helpers";
import currencyData from "constants/currencyConstants";
import useCustomWindowOpen from "@Components/CustomWindowOpen";
import useCustomWindowLocation from "@Components/CustomWindowLocation";
import AgentQRSection from "../AgentQRSection";
import SalesOfferSectionMobile from "../SalesOfferSection/SalesOfferSectionMobile";
import AgentContainer from "@Pages/Support/Component/AgentContainer";
import { getLeadData } from "services/leadsHelper";
import { encryptDataString } from "helpers/encryption";
import { markPdpCtaEngaged } from "@Components/PdpAppDownloadBottomSheet/pdpCtaEngagement";

const getInitials = (name?: string): string => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
};

const PropertyCTAs = ({ isDesktop = false }) => {
  const customWindowOpen = useCustomWindowOpen();
  const customWindowLocation = useCustomWindowLocation();
  const {
    isPartnered,
    location,
    data,
    isLFOneFilled,
    region,
    isLeadFormFilled,
    personalDetails,
    meta_facts,
    filters,
    meta_community_facts,
    meta_general_facts,
    inventoriesData,
    rooms,
    isRoomTypeLoading,
    isInventoryDataLoaded,
    utmSource,
    currencyExchangeRates,
    openLeadFormModal,
  } = useSelector(
    ({ propertyPageData, leadFormModal, searchPageData, userHistory, appData }: AppState) => ({
      isPartnered:
        propertyPageData.data?.meta?.is_partnered ??
        propertyPageData.data?.meta_short?.is_partnered,
      location: propertyPageData.data?.location,
      data: propertyPageData.data,
      meta_facts: propertyPageData.data?.meta_facts,
      meta_community_facts: propertyPageData.data?.meta_community_facts,
      meta_general_facts: propertyPageData.data?.meta_general_facts,
      isLFOneFilled: leadFormModal.isLFOneFilled,
      region: searchPageData.region,
      isLeadFormFilled: leadFormModal.isLeadFormFilled,
      personalDetails: leadFormModal.personalDetails,
      filters: propertyPageData.filters,
      inventoriesData: userHistory.inventoriesData,
      rooms: propertyPageData.roomTypes.data,
      isRoomTypeLoading: propertyPageData.roomTypes.isLoading,
      isInventoryDataLoaded: userHistory.isInventoryDataLoaded,
      utmSource: appData.UtmDetails?.utm_source,
      currencyExchangeRates: appData.currencyExchangeRates,
      openLeadFormModal: leadFormModal.openLeadFormModal,
    }),
    shallowEqual,
  );
  const { isWhitelabel, isDemoSubdomain, partner_uuid, partner_id } = useWhitelabel();
  const { trackEnquireNow, trackPropertyView } = useAnalytics();
  const history = useHistory();
  const { t } = useTranslationHook();
  const { isBelow } = useResponsiveness();
  const isMobile = isBelow(SCREEN_SIZE.SM);
  const isApp = useIsApp();
  const isMiniProgram = useIsMiniProgram();
  const isOrganic = utmSource === "organic" || !utmSource;
  const isTabletPortrait = useIsTabletPortrait();
  const showWeChatButton =
    config.IS_CHINA_APP && !isMiniProgram && !isDesktop && isOrganic && !isTabletPortrait;
  const {
    VIEW_BOOKINGS,
    CONTINUE_BOOKING,
    CONTINUE_PAYMENT,
    ENQUIRED,
    ENQUIRE_NOW,
    CONTINUE_APPLY,
  } = HAPPY_FLOW_TENANCY_CTAS;
  const t_VIEW_BOOKINGS = t(VIEW_BOOKINGS);
  const t_CONTINUE_BOOKING = t(CONTINUE_BOOKING);
  const t_CONTINUE_PAYMENT = t(CONTINUE_PAYMENT);
  const t_ENQUIRED = t(ENQUIRED);
  const t_ENQUIRE_NOW = t(ENQUIRE_NOW);
  const t_CONTINUE_APPLY = t(CONTINUE_APPLY);

  const [showViewRooms, setShowViewRooms] = useState(true);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showAgentAvatar, setShowAgentAvatar] = useState(false);
  const [agentName, setAgentName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [show, setShow] = useState(false);
  const isWhitelabelPartnerInventory = isWhitelabelPartnerInventoryFunc(data, partner_id);
  const dispatch = useDispatch();
  const isHappyFlow = useIsHappyFlow();
  const isHappyFlowBookForm = useIsHappyFlowBookForm();
  const isPaymentBeforeBookform =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.ENABLE_PAYMENT_BEFORE_BF) &&
    data?.payment_before_bookform &&
    isPaymentActive(data);
  const isZendeskFlowAfterLead = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.POST_LEAD_SUBMISSION_ZENDESK_FLOW,
  );
  const isApplyNowCTA = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.APPLY_NOW_CTA);
  const openLeadModalFromBookCTA = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.OPEN_LEAD_MODAL_FROM_BOOK_CTA,
  );
  const isBookFormEnabledForInactiveProperty = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.ENABLE_BOOK_FORM_FOR_INACTIVE_PROPERTY,
  );
  const showAgentDetails = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_AGENT_DETAILS);
  const propertyPageDispatcher = new PropertyPageDispatcher(dispatch);
  const defaultCTATextWithExperiment = ["View Rooms", "See Availability", "See Availability"];
  const searchPageCtaExperimentValue = useFeatureValue(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.SEARCH_PROPERTY_PAGE_CTA_EXPERIMENT_VALUE,
    config.IS_CHINA_APP ? 0 : -1,
  );
  let viewRoomsText = t(defaultCTATextWithExperiment[searchPageCtaExperimentValue]);

  const handleShowViewRooms = () => setShowViewRooms(true);

  const handleHideViewRooms = () => setShowViewRooms(false);

  useEffect(() => {
    window.addEventListener("room_type_section_in_view", handleHideViewRooms);
    window.addEventListener("room_type_section_not_in_view", handleShowViewRooms);

    return () => {
      window.removeEventListener("room_type_section_in_view", handleHideViewRooms);
      window.removeEventListener("room_type_section_not_in_view", handleShowViewRooms);
    };
  }, []);

  useEffect(() => {
    if (!isDesktop && showAgentDetails && !config.IS_CHINA_APP && typeof window !== "undefined") {
      const leadDataString = localStorage.getItem("lead-form-one-success-data");
      if (leadDataString) {
        try {
          const leadData = JSON.parse(leadDataString);
          if (leadData?.uuid) {
            const encryptedLeadUUID =
              encryptDataString(leadData.uuid, "lead_user_lead_uuid").split("=")[1] || "";
            const params = {
              methods: "contact_agent_details",
              cache: false,
              sort_key: "created",
              sort_order: "desc",
              not_state: "lost",
            };
            getLeadData(encryptedLeadUUID, params)
              .then((res: any) => {
                if (res?.data?.contact_agent_details?.name) {
                  setShowAgentAvatar(true);
                  setAgentName(res.data.contact_agent_details.name);
                }
              })
              .catch(() => {});
          }
        } catch {
          // invalid JSON, ignore
        }
      }
    }
  }, [isDesktop, isLFOneFilled, showAgentDetails]);

  useEffect(() => {
    if (isHappyFlow) {
      setShow(isInventoryDataLoaded);
    } else {
      setShow(true);
    }
  }, [isInventoryDataLoaded, isHappyFlow]);

  useEffect(() => {
    if (data?.id && typeof window !== "undefined") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const win = window as any;
      // Only schedule once — use window flag to prevent multiple timers
      if (!win.__amberPvTimerSet) {
        win.__amberPvTimerSet = true;
        setTimeout(() => {
          win.__amberPvTimerSet = false;
          trackPropertyView(String(data.id), data.name);
        }, 1000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  const enquireClickHandler = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    markPdpCtaEngaged();
    if (isApp) {
      logAppEvent({
        eventName: APP_EVENTS.LEAD_CTA_CLICKED,
        payload: {
          cta_name: "enquire_now",
          screen_name: "",
          section_name: "",
          lead_flow: "",
        },
      });
    }

    if (isPartnered) {
      trackEnquireNow(String(data?.id), "property", data?.name);
      gtagDuplicateEventEmitter({
        action: "clicked_book_enquire_now_property_newleadform",
        category: PAGE.INVENTORY,
        label: "property_sidebar",
        page: PAGE.INVENTORY,
        location: location?.locality?.long_name,
      });
      gtagEventEmitter({
        action: "clicked_book_enquire_now_property",
        category: PAGE.INVENTORY,
        label: "property_sidebar",
        page: PAGE.INVENTORY,
        location: location?.locality?.long_name,
      });

      (async () => {
        if (isHappyFlow && isLFOneFilled && !isWhitelabelPartnerInventory) {
          setIsLoading(true);
          await handleLeadAPICall({
            dispatch,
            region,
            inventory: data,
            isLeadFormFilled,
            inModal: true,
            openingSource: "clicked_book_enquire_now_property",
            formData: personalDetails,
            isLFOneFilled,
            filters,
            history,
            propertyId: data.id,
            isPaymentBeforeBookform,
            isZendeskFlowAfterLead,
            redirectWithoutSuccessScreen: openLeadModalFromBookCTA,
            isDesktop,
            isBookFormEnabledForInactiveProperty,
            isWhitelabel,
            isDemoSubdomain,
            partner_uuid,
            isMobile,
          });
          setIsLoading(false);
        }
        if (!isLFOneFilled || !isHappyFlow || isWhitelabelPartnerInventory) {
          const leadFormDispatcher = new LeadFormDispatcher(dispatch);
          leadFormDispatcher.handleOnEnquireClick({
            data,
            source: "clicked_book_enquire_now_property",
            page: 1,
          });
        }
      })();
    } else {
      gtagEventEmitter({
        action: "nonpartnerproperty_redirected",
        category: PAGE.INVENTORY,
        label: data?.name,
      });
      customWindowOpen({
        url: isWhitelabel
          ? convertToWhitelabelURL(data?.source_link, isDemoSubdomain, partner_uuid)
          : data?.source_link,
        target: "_blank",
        isExternal: true,
      });
    }
  };

  const bookingClickHandler = (dataObj: actionPayload) => () => {
    markPdpCtaEngaged();
    gtagEventEmitter({
      action: "clicked_continue_booking_property",
      category: PAGE.INVENTORY,
      label: "property_sidebar",
      page: PAGE.INVENTORY,
      location: location?.locality?.long_name,
    });
    const paymentBeforeBookformParam =
      isPaymentBeforeBookform &&
      !(dataObj?.link && dataObj.link.includes("payment_before_bookform=true"))
        ? "&payment_before_bookform=true"
        : "";

    const link = dataObj?.link
      ? `/${dataObj.link}`
      : getBookFormLink({
          bookFormInitiated: dataObj || {},
          canonicalName: data.canonical_name,
        });

    const targetLink = isWhitelabel
      ? convertToWhitelabelURL(
          `${link}${paymentBeforeBookformParam}`,
          isDemoSubdomain,
          partner_uuid,
        )
      : `${link}${paymentBeforeBookformParam}`;

    if (isHappyFlowBookForm) {
      viewTransitionNavUtility(() => history.push(targetLink), "right-to-left");
    } else {
      customWindowOpen({ url: targetLink, target: "_self" });
    }
  };

  const paymentClickHandler = (dataObj: actionPayload) => () => {
    gtagEventEmitter({
      action: "clicked_continue_payment_property",
      category: PAGE.INVENTORY,
      label: "property_sidebar",
      page: PAGE.INVENTORY,
      location: location?.locality?.long_name,
    });
    const paymentBeforeBookformParam =
      isPaymentBeforeBookform &&
      !(dataObj?.link && dataObj.link.includes("payment_before_bookform=true"))
        ? "&payment_before_bookform=true"
        : "";
    const link = dataObj?.link
      ? `/${dataObj.link}`
      : getPaymentFormLink({
          rooms,
          id: dataObj?.id || 0,
          canonicalName: data.canonical_name,
          payment_initiated: dataObj,
        });

    const targetLink = isWhitelabel
      ? convertToWhitelabelURL(
          `${link}${paymentBeforeBookformParam}`,
          isDemoSubdomain,
          partner_uuid,
        )
      : `${link}${paymentBeforeBookformParam}`;

    if (isHappyFlowBookForm) {
      viewTransitionNavUtility(() => history.push(targetLink), "right-to-left");
    } else {
      customWindowOpen({ url: targetLink, target: "_self" });
    }
  };

  const bookingsPageLink = isWhitelabel
    ? convertToWhitelabelURL("/profile/leads", isDemoSubdomain, partner_uuid)
    : "/profile/leads";

  const { label, handler } = useMemo(() => {
    if (isPartnered) {
      if (
        isHappyFlow &&
        isPaymentBeforeBookform &&
        inventoriesData.id === data.id &&
        !isWhitelabelPartnerInventory
      ) {
        if (inventoriesData.payment_completed?.id && inventoriesData.book_form_completed?.id)
          return {
            label: t_VIEW_BOOKINGS,
            handler: () => {
              if (isApp) history.push(bookingsPageLink);
              else customWindowOpen({ url: bookingsPageLink, target: "_blank" });
            },
          };

        if (inventoriesData.payment_completed)
          return {
            label: t_CONTINUE_BOOKING,
            handler: bookingClickHandler(inventoriesData.payment_completed),
          };

        if (inventoriesData.payment_initiated)
          return {
            label: t_CONTINUE_PAYMENT,
            handler: paymentClickHandler(inventoriesData.payment_initiated),
          };

        if (inventoriesData.book_form_initiated)
          return {
            label: isApplyNowCTA ? t_CONTINUE_APPLY : t_CONTINUE_BOOKING,
            handler: bookingClickHandler(inventoriesData.book_form_initiated),
          };

        if (inventoriesData.enquired)
          return {
            label: t_ENQUIRED,
            handler: enquireClickHandler,
          };
      } else if (
        isHappyFlow &&
        inventoriesData.id === data.id &&
        !isPaymentBeforeBookform &&
        !isWhitelabelPartnerInventory
      ) {
        if (
          inventoriesData.payment_completed?.id ||
          inventoriesData.book_form_completed?.label === VIEW_BOOKINGS
        ) {
          return {
            label: t_VIEW_BOOKINGS,
            handler: () => {
              if (isApp) history.push(bookingsPageLink);
              else customWindowOpen({ url: bookingsPageLink, target: "_blank" });
            },
          };
        }

        if (inventoriesData.payment_initiated) {
          return {
            label: t_CONTINUE_PAYMENT,
            handler: paymentClickHandler(inventoriesData.payment_initiated),
          };
        }

        if (inventoriesData.book_form_completed) {
          return {
            label: t_CONTINUE_PAYMENT,
            handler: paymentClickHandler(inventoriesData.book_form_completed),
          };
        }

        if (inventoriesData.book_form_initiated) {
          return {
            label: isApplyNowCTA ? t_CONTINUE_APPLY : t_CONTINUE_BOOKING,
            handler: bookingClickHandler(inventoriesData.book_form_initiated),
          };
        }
        if (inventoriesData.enquired)
          return {
            label: t_ENQUIRED,
            handler: enquireClickHandler,
          };
      }
      return {
        label: t_ENQUIRE_NOW,
        handler: enquireClickHandler,
      };
    }
    if (isPartnered === false) {
      return {
        label: t("searchpage.components.searchlistitem.bottomsection.visitsitebutton"),
        handler: enquireClickHandler,
      };
    }
    return {
      label: t_ENQUIRE_NOW,
      handler: enquireClickHandler,
    };
  }, [
    inventoriesData.id,
    inventoriesData.enquired,
    inventoriesData.payment_completed,
    inventoriesData.book_form_initiated,
    inventoriesData.payment_initiated,
    isHappyFlow,
    isPartnered,
    isWhitelabelPartnerInventory,
  ]);

  const isViewRoomsPrimaryCTA =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.VIEW_ROOM_PRIMARY_CTA) && label === t_ENQUIRE_NOW;

  const getRoomInfo = (dataObj: actionPayload) => {
    const { config_name, meta, pricing, level } = dataObj;

    let price = getAvailableStartingPricingHelper(pricing);
    let currency = getPricingCurrency(pricing?.currency) ?? "";

    if (currencyExchangeRates?.baseCurrency) {
      const currentCurrencyObject = currencyData?.find((c) => c.symbol === currency);

      const exchangeRate = currencyExchangeRates?.rates?.find(
        (rate) =>
          rate.target_currency?.toLowerCase() ===
          currentCurrencyObject?.standard_name?.toLowerCase(),
      );

      if (exchangeRate?.rate) {
        price = Math.ceil(Number(price) / exchangeRate.rate);
        currency = getCurrencySymbol(currencyExchangeRates?.baseCurrency);
      }
    }

    return {
      roomName: config_name,
      tenancyPrice: `${currency}${price}`,
      tenancyDuration: getPricingDuration(pricing?.duration) || "",
      leaseDuration: level === "config" ? "Flexible" : meta?.lease_duration || "",
    };
  };

  const { roomName, tenancyPrice, tenancyDuration, leaseDuration } = useMemo(() => {
    const { payment_completed, payment_initiated, book_form_completed, book_form_initiated } =
      inventoriesData;
    switch (label) {
      case t_CONTINUE_PAYMENT:
        return getRoomInfo(
          // @ts-ignore
          isPaymentBeforeBookform ? payment_initiated : payment_initiated || book_form_completed,
        );

      case t_CONTINUE_BOOKING:
        return getRoomInfo(
          // @ts-ignore
          isPaymentBeforeBookform ? book_form_initiated || payment_completed : book_form_initiated,
        );
      case t_CONTINUE_APPLY:
        return getRoomInfo(
          // @ts-ignore
          isPaymentBeforeBookform ? book_form_initiated || payment_completed : book_form_initiated,
        );
      default:
        return { roomName: "", tenancyPrice: "", tenancyDuration: "", leaseDuration: "" };
    }
  }, [label]);

  const showTenancyInfo =
    !isDesktop &&
    (label === CONTINUE_BOOKING || label === CONTINUE_PAYMENT || label === CONTINUE_APPLY);
  const updatedShowViewRooms = showTenancyInfo ? false : showViewRooms || showAgentAvatar;
  const hideSection = !isDesktop && !showViewRooms && label === ENQUIRED && !showAgentAvatar;

  if ([1, 2].includes(searchPageCtaExperimentValue) && label === t_ENQUIRED) {
    viewRoomsText = isApplyNowCTA ? CONTINUE_APPLY : t_CONTINUE_BOOKING;
  }

  const chatModalHandler = () => {
    if (isMiniProgram) {
      customWindowLocation({ url: `${window.location.origin}/chat` });
      return;
    }
    setShowChatModal(true);
  };

  return (
    <>
      {!isDesktop && !hideSection && show && (
        <SalesOfferSectionMobile inventoryMeta={data?.meta || data?.meta_short} />
      )}
      <div
        className={formatClassNames(
          styles.container,
          isDesktop && styles.desktop,
          isHappyFlow && styles.happyFlowContainer,
          !openLeadFormModal && (hideSection || (!isDesktop && !show)) && styles.hideSection,
        )}
      >
        {isDesktop && (
          <div className={styles.detailHeader}>
            <div className={styles.textContainer}>
              <p className={styles.propertyName}>{data?.name}</p>
            </div>
            <div data-testid="propertypage-icons-container" className={styles.iconsContainer}>
              <ShortlistButton
                inventoryId={data?.id}
                noAbosolute
                dataTestId="propertypage-property-shortlist-icon"
                inventory={data}
                screenName={APP_PAGE.PROPERTY}
              />
              <div
                data-testid="propertypage-property-share-icon"
                className={styles.shareIcon}
                role="none"
                onClick={() => {
                  gtagEventEmitter({
                    action: "clicked_share",
                    category: PAGE.INVENTORY,
                    label: data?.id,
                  });
                  propertyPageDispatcher.showShareModal(true);
                }}
              >
                <ShareIcon />
              </div>
            </div>
          </div>
        )}
        {!isDesktop &&
          (showAgentAvatar ? (
            <div
              className={styles.agentAvatarBtn}
              role="button"
              tabIndex={0}
              onClick={() => setShowAgentModal(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter") setShowAgentModal(true);
              }}
              data-testid="propertypage-agent-avatar"
              aria-label="View assigned agent details"
            >
              <div className={styles.agentAvatarCircle}>
                <span className={styles.agentInitials}>{getInitials(agentName)}</span>
                <div className={styles.agentOnlineDot} />
              </div>
            </div>
          ) : (
            <Button
              dataTestId="propertypage-support"
              variant="outline"
              size="lg"
              className={styles.supportBtn}
              onClick={chatModalHandler}
            >
              <LottieAnimator
                animationData="Call_Center.json"
                renderer="svg"
                id="propertypage-support"
                width="40px"
                height="40px"
              />
            </Button>
          ))}

        {updatedShowViewRooms && (
          <Button
            variant={
              label === t_ENQUIRED || isViewRoomsPrimaryCTA || viewRoomsText === "See Availability"
                ? isWhitelabel
                  ? "whiteLabel_primary"
                  : "primary"
                : "outline"
            }
            size="lg"
            dataTestId="propertypage-view-rooms"
            className={formatClassNames(
              styles.btn,
              isDesktop && styles.desktop,
              label === t_ENQUIRED && styles.forTabletView,
              config.IS_CHINA_APP && styles.forChinaTabView,
            )}
            onClick={() => viewRoomsClickHandler(viewRoomsText)}
          >
            {searchPageCtaExperimentValue === -1 ? (
              <Skeleton height="14px" width="120px" addBorderRadius />
            ) : (
              viewRoomsText
            )}
          </Button>
        )}
        {showTenancyInfo &&
          (isRoomTypeLoading ? (
            <Skeleton addBorderRadius width="100%" height="46px" />
          ) : (
            <div className={styles.tenancyInfoContainer}>
              <div className={styles.roomName}>{roomName}</div>
              <div className={styles.info}>
                {leaseDuration === "Flexible"
                  ? leaseDuration
                  : `${leaseDuration}${tenancyDuration}`}{" "}
                {tenancyPrice}/{tenancyDuration}
              </div>
            </div>
          ))}
        {label !== t_ENQUIRED &&
          (showWeChatButton ? (
            <WeChatButton className={styles.weChatbtn} page={PAGE.INVENTORY} />
          ) : (
            <Button
              variant={
                isViewRoomsPrimaryCTA || viewRoomsText === "See Availability"
                  ? "outline"
                  : isWhitelabel
                  ? "whiteLabel_primary"
                  : "primary"
              }
              size="lg"
              onClick={handler}
              className={formatClassNames(
                styles.btn,
                !updatedShowViewRooms && styles.forTabletView,
                config.IS_CHINA_APP && styles.forChinaTabView,
              )}
              {...(isPartnered && isLFOneFilled && isHappyFlow && { showLoader: isLoading })}
              dataTestId={
                isPartnered ? "propertypage-enquire-button" : "propertypage-visit-site-button"
              }
            >
              {label}
            </Button>
          ))}
        {/* {isDesktop && viewCount && (
          <div className={styles.propertyViewed}>
            <EyeFactsIcon width={18} height={18} fill="#3F83F8" />
            {viewCount} students viewed this property in last 24 hours
          </div>
        )} */}
        {isDesktop && meta_facts?.length > 0 ? (
          <MetaFacts
            metaCommunityFacts={meta_community_facts || {}}
            metaGeneralFacts={meta_general_facts || meta_facts || []}
            fromProperty
            isDesktop
          />
        ) : (
          <></>
        )}
        {/* {isDesktop && <div className={styles.subtext}>{`You won't be charged yet`}</div>} */}
        {isDesktop && config.IS_CHINA_APP && (
          <>
            <hr className={styles.dottedDivider} />
            <AgentQRSection />
          </>
        )}
      </div>

      {showChatModal && (
        <PropertyDetailsModal
          header={t("Chat Via")}
          removeFooter
          hidePropertyModal={() => setShowChatModal(false)}
          isDesktop={isDesktop}
        >
          <ChatLinks onClose={() => setShowChatModal(false)} />
        </PropertyDetailsModal>
      )}

      {showAgentModal && (
        <BottomSheetOverlay
          hideBottomSheetOverlay={() => setShowAgentModal(false)}
          size="fitContent"
          isHistoryPush
          name="AgentDetailsModal"
        >
          <AgentContainer isPropertyPageConnectModal onClose={() => setShowAgentModal(false)} />
        </BottomSheetOverlay>
      )}
    </>
  );
};

export default wrapperHOC(PropertyCTAs, {
  componentName: "PropertyCTAs-PropertyPage",
  showForChina: true,
});
